import { BrowserMicroSentryClient } from '@micro-sentry/browser'
import type { SentryRequestBody } from '@micro-sentry/core'
import type { Config } from '@/core/types'
import ConfigReader from '@/core/configReader'
import { LogLevel } from '@/core/logger/types'
import { getUnhandledRejectionEventHandler } from '@/utils/unhandledRejection'

class SentryMonitoringService {
  constructor() {
    this.config = ConfigReader.config()
  }

  private readonly config: Config

  private microSentryClient?: BrowserMicroSentryClient

  private readonly ignoreErrors = [
    'ResizeObserver loop limit exceeded', // We can safely ignore this error: see https://github.com/WICG/resize-observer/issues/38 for details
    'ResizeObserver loop completed with undelivered notifications.', // A variant of the above error, see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
    'Non-Error promise rejection captured', // https://github.com/getsentry/sentry-javascript/issues/3440
    'Object captured as exception with keys: code, message',
    'Object captured as promise rejection with keys: code, message',
  ]

  public init(appName?: string): void {
    this.microSentryClient = new BrowserMicroSentryClient({
      dsn: this.config.sentry.dsn,
      environment: APP_BUILD_ENV,
      release: [APP_NAME, APP_VERSION].join('@'),
      ignoreErrors: this.ignoreErrors,
      beforeSend: (request: SentryRequestBody) => {
        if (
          request.exception?.values?.some((v) => {
            return v.stacktrace?.frames?.some((f) => !f.filename?.includes('textmagic.com'))
          })
        ) {
          return null as unknown as SentryRequestBody // don't send to Sentry
        }
        if (appName) {
          request.tags ??= {}
          request.tags.appName = appName
        }
        return request
      },
    })

    window.addEventListener('error', this.errorEventHandler.bind(this))
    window.addEventListener('unhandledrejection', getUnhandledRejectionEventHandler(this.logError.bind(this)))
  }

  public setCommonExtras(extras: Partial<Record<string, unknown>>) {
    this.microSentryClient?.setExtras(extras)
  }

  public logError(error: Error, logLevel: LogLevel = LogLevel.ERROR) {
    if (logLevel === LogLevel.NULL) return
    this.microSentryClient?.report(error)
  }

  private errorEventHandler(evt: ErrorEvent) {
    let err: any = evt
    if (!evt?.message) {
      err = { ...evt, message: "The error was caused due to an 'error' event", stack: evt.error }
    }
    this.logError(err)
    return false
  }
}

export default new SentryMonitoringService()
